/**
 * Configurações centralizadas para o Framework Realtime
 * 
 * Define padrões de queries, tabelas intent-first e opções de fallback
 */

import type { RealtimeTable } from '@/contexts/RealtimeContext';

/**
 * Configuração de query para tabelas com Realtime puro
 * Usa staleTime: Infinity para confiar 100% no WebSocket
 */
export const REALTIME_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
} as const;

/**
 * Configuração de query para modo fallback (polling)
 * Ativado quando WebSocket falha por mais de 10s
 */
export const FALLBACK_QUERY_OPTIONS = {
  staleTime: 30_000, // 30 segundos
  gcTime: 5 * 60 * 1000, // 5 minutos
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;

/**
 * Intervalo de polling quando em modo fallback (ms)
 * 30 segundos - otimizado para bateria em ambiente de campo
 * Realtime via WebSocket continua instantâneo; fallback é último recurso
 */
export const FALLBACK_POLLING_INTERVAL = 30_000;

/**
 * Tabelas que usam arquitetura Intent-First
 * 
 * Essas tabelas:
 * - Usam optimistic updates via setQueryData
 * - NÃO devem usar invalidateQueries no RealtimeContext
 * - O banco é confirmação, não gatilho de UI
 */
export const INTENT_FIRST_TABLES: RealtimeTable[] = [
  'pedidos',
  'mensagens_cliente' as RealtimeTable, // Subscription direta
];

/**
 * Tabelas que usam patch direto no cache
 * 
 * Essas tabelas:
 * - Recebem eventos Realtime e aplicam setQueryData diretamente
 * - Evitam refetch completo para latência mínima
 */
export const DIRECT_PATCH_TABLES: RealtimeTable[] = [
  'pedidos',
  'ocorrencias',
  'estoque_diario',
  'lembretes',
  'fiados',
  'fiado_pagamentos',
  'fluxo_caixa',
  'cardapio',
  'clientes',
  'solicitacoes_cancelamento',
  'avisos_usuario',
  'depositos_meta',
  'solicitacoes_despesa',
  'checkin_feedbacks',
  'cofre',
  'despesas_recorrentes',
  'clube_metricas',
  'clube_ranking',
  'clube_pontos',
  'contatos_whatsapp',
  'entregas' as RealtimeTable,
  'entrega_ofertas' as RealtimeTable,
  'entregadores' as RealtimeTable,
];

/**
 * Configuração de throttle para eventos de alta frequência
 */
export const REALTIME_THROTTLE_CONFIG = {
  /** Intervalo mínimo entre eventos da mesma tabela (ms) */
  minInterval: 100,
  /** Tempo para resetar contador de eventos (ms) */
  resetInterval: 5000,
  /** Máximo de eventos antes de throttle (por tabela) */
  maxEventsBeforeThrottle: 50,
} as const;

/**
 * Configuração de reconexão exponencial
 */
export const RECONNECT_CONFIG = {
  /** Delay base para primeira reconexão (ms) */
  baseDelay: 5000,
  /** Fator de multiplicação para backoff */
  backoffFactor: 1.5,
  /** Delay máximo entre tentativas (ms) */
  maxDelay: 30000,
  /** Número máximo de tentativas antes de desistir */
  maxAttempts: 10,
} as const;

/**
 * Helper para calcular delay de reconexão com backoff exponencial
 */
export function getReconnectDelay(attempt: number): number {
  const delay = RECONNECT_CONFIG.baseDelay * Math.pow(RECONNECT_CONFIG.backoffFactor, attempt);
  return Math.min(delay, RECONNECT_CONFIG.maxDelay);
}

/**
 * Verifica se uma tabela usa arquitetura Intent-First
 */
export function isIntentFirstTable(table: RealtimeTable): boolean {
  return INTENT_FIRST_TABLES.includes(table);
}

/**
 * Verifica se uma tabela usa patch direto
 */
export function isDirectPatchTable(table: RealtimeTable): boolean {
  return DIRECT_PATCH_TABLES.includes(table);
}
